// Audio file storage for offline books. OPFS (disk-backed, seekable File
// objects) when the browser has it; IndexedDB chunk records otherwise.
// Paths look like offline/<bookKey>/<chapterId>.mp3.
import { dbPut, dbGetAll, dbGetAllByIndex, dbDeleteByIndex } from './db';

const AUDIO_TYPE = 'audio/mpeg';

// createSyncAccessHandle only exists inside dedicated workers, so the main
// thread can only check for OPFS itself; the worker probes the handle API and
// the backend falls back to IndexedDB at runtime if it is missing.
export function opfsSupported() {
  try {
    return typeof navigator !== 'undefined'
      && !!navigator.storage
      && typeof navigator.storage.getDirectory === 'function'
      && typeof Worker !== 'undefined';
  } catch (e) {
    return false;
  }
}

let resolvedKind = null;

class OpfsBackend {
  constructor() {
    this.worker = null;
    this.pending = new Map();
    this.nextId = 1;
    this.fallback = null;
    this.probe = null;
  }

  // First use: ask the worker whether sync access handles exist. If not,
  // every call is delegated to the IndexedDB backend from then on.
  async ready() {
    if (this.fallback) return this.fallback;
    if (!this.probe) {
      this.probe = this.call('probe', {}).then((ok) => {
        if (!ok) {
          this.fallback = new IdbBackend();
          resolvedKind = 'indexeddb';
        } else {
          resolvedKind = 'opfs';
        }
        return this.fallback;
      }).catch((error) => {
        console.warn('Offline storage: OPFS worker unavailable, using IndexedDB.', error);
        this.fallback = new IdbBackend();
        resolvedKind = 'indexeddb';
        return this.fallback;
      });
    }
    return this.probe;
  }

  ensureWorker() {
    if (this.worker) return this.worker;
    this.worker = new Worker(new URL('./opfs.worker.js', import.meta.url));
    this.worker.onmessage = (event) => {
      const { id, ok, result, error } = event.data || {};
      const entry = this.pending.get(id);
      if (!entry) return;
      this.pending.delete(id);
      if (ok) entry.resolve(result); else entry.reject(new Error(error || 'OPFS worker error'));
    };
    this.worker.onerror = (event) => {
      const error = new Error(event.message || 'OPFS worker crashed');
      for (const entry of this.pending.values()) entry.reject(error);
      this.pending.clear();
      this.worker = null;
    };
    return this.worker;
  }

  call(op, args, transfer) {
    const worker = this.ensureWorker();
    const id = this.nextId++;
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      worker.postMessage({ id, op, args }, transfer || []);
    });
  }

  async writeChunk(path, offset, buffer) {
    const fb = await this.ready();
    if (fb) return fb.writeChunk(path, offset, buffer);
    return this.call('write', { path, offset, buffer }, [buffer]);
  }

  async truncate(path, size) {
    const fb = await this.ready();
    if (fb) return fb.truncate(path, size);
    return this.call('truncate', { path, size });
  }

  async size(path) {
    const fb = await this.ready();
    if (fb) return fb.size(path);
    return this.call('size', { path });
  }

  async close(path) {
    const fb = await this.ready();
    if (fb) return fb.close(path);
    return this.call('close', { path });
  }

  async remove(path) {
    const fb = await this.ready();
    if (fb) return fb.remove(path);
    return this.call('remove', { path });
  }

  async removeDir(path) {
    const fb = await this.ready();
    if (fb) return fb.removeDir(path);
    return this.call('removeDir', { path });
  }

  async getFile(path) {
    const fb = await this.ready();
    if (fb) return fb.getFile(path);
    const parts = path.split('/').filter(Boolean);
    const fileName = parts.pop();
    let dir = await navigator.storage.getDirectory();
    for (const part of parts) dir = await dir.getDirectoryHandle(part, { create: false });
    const handle = await dir.getFileHandle(fileName, { create: false });
    return handle.getFile();
  }
}

class IdbBackend {
  async writeChunk(path, offset, buffer) {
    const blob = new Blob([buffer], { type: AUDIO_TYPE });
    await dbPut('chunks', { id: `${path}#${String(offset).padStart(12, '0')}`, path, offset, size: blob.size, blob });
    return blob.size;
  }

  async truncate(path, size) {
    if (size === 0) await dbDeleteByIndex('chunks', 'path', path);
    return size;
  }

  async size(path) {
    const chunks = await dbGetAllByIndex('chunks', 'path', path);
    // Contiguous writes only: the size is the end of the last chunk.
    let end = 0;
    for (const chunk of chunks) end = Math.max(end, chunk.offset + chunk.size);
    return end;
  }

  async close() { return true; }

  async remove(path) {
    await dbDeleteByIndex('chunks', 'path', path);
    return true;
  }

  async removeDir(path) {
    const all = await dbGetAll('chunks');
    const prefix = path + '/';
    const paths = Array.from(new Set(all.filter((c) => c.path.startsWith(prefix)).map((c) => c.path)));
    await Promise.all(paths.map((p) => dbDeleteByIndex('chunks', 'path', p)));
    return true;
  }

  async getFile(path) {
    const chunks = await dbGetAllByIndex('chunks', 'path', path);
    chunks.sort((a, b) => a.offset - b.offset);
    if (!chunks.length) throw new Error('File not found');
    return new Blob(chunks.map((c) => c.blob), { type: AUDIO_TYPE });
  }
}

let backend = null;
export function getStorage() {
  if (!backend) backend = opfsSupported() ? new OpfsBackend() : new IdbBackend();
  return backend;
}

export function storageKind() {
  if (resolvedKind) return resolvedKind;
  return opfsSupported() ? 'opfs?' : 'indexeddb';
}

/** Resolve the backend (runs the worker probe) and return its kind. */
export async function resolveStorageKind() {
  const storage = getStorage();
  if (storage instanceof OpfsBackend) await storage.ready();
  else resolvedKind = 'indexeddb';
  return resolvedKind;
}

// Letters, digits, _ . - only: WebKit's OPFS rejects names Chrome accepts
// (book keys contain ':').
const safeSegment = (value) => String(value).replace(/[^a-zA-Z0-9_.-]/g, '_');

export function bookDir(bookKey) {
  return `offline/${safeSegment(bookKey)}`;
}

export function chapterPath(bookKey, chapterId) {
  return `${bookDir(bookKey)}/${safeSegment(chapterId)}.mp3`;
}
